import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { checkReadyToBid } from '@/components/GridItemCard/Card/helpers/checkReadyToBid';
import { ItemCardComponent, ItemCardInfo } from '@/components/GridItemCard/types/types';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppSelector } from '@/redux/hooks';
import { getUserIsInGoodStanding } from '@/redux/modules/account/user/user.selectors';
import { getBidLimitBalance } from '@/redux/modules/bidLimit/bidLimit.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getCatalogNetworkStatus,
    getLatestMessageTimestamp,
    getLiveCatalogClosed,
    getLiveCatalogCurrentItemId,
    getLiveCatalogCurrentItemIndex,
    getLiveCatalogStatus,
    getLiveCatalogWon,
    getLiveItemDataCurrentAskPrice,
} from '@/redux/modules/console';
import {
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIndex,
    getItemSummaryLeadingBid,
    getItemSummaryPromoted,
    getItemSummarySalePrice,
    getItemSummaryStartPrice,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemCardSource, useTrackItemCardClicked } from '@/utils/amplitude/analytics';
import { isMyBidsPage, isSavedItemsPage } from '@/utils/urls';

import { CardGridBody } from './components/CardGridBody/CardGridBody';
import CardImage from './components/CardImage/CardImage';

const TimedPlusMyItemsCard: ItemCardComponent = ({ className, itemId }) => {
    const trackItemCardClicked = useTrackItemCardClicked();

    const { pathname } = useLocation();

    const accountInGoodStanding = useAppSelector(getUserIsInGoodStanding);

    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));
    const salePrice = useAppSelector((state) => getItemSummarySalePrice(state, itemId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));

    const [hover, setHover] = useState(false);
    const [linkHover, setLinkHover] = useState(false);
    const isMobile = useIsMobileLayout();
    const currentItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const comingUp = catalogStatus === 'live' && currentItemIndex <= index;
    const isDone = catalogStatus === 'done';
    const currentItemLive = comingUp && currentItemIndex === index;

    const { approved, bidLimit, blockedByAuctioneer, declined, limited, suspended } = useAppSelector((state) =>
        getCatalogRegistration(state, catalogId)
    );

    const closed = useAppSelector((state) => getLiveCatalogClosed(state, catalogId));
    const won = useAppSelector((state) => getLiveCatalogWon(state, catalogId));
    const status = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const currentItem = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const currentAskPrice = useAppSelector((state) => getLiveItemDataCurrentAskPrice(state, itemId));
    const liveUpdateTimestamp = useAppSelector((state) => getLatestMessageTimestamp(state, catalogId));
    const bidLimitBalance = useAppSelector((state) => getBidLimitBalance(state, catalogId));
    const currentPrice = leadingBid || salePrice || startPrice || 0;
    const overBidLimit = limited ? bidLimitBalance + currentPrice > bidLimit : false;
    const networkStatus = useAppSelector((state) => getCatalogNetworkStatus(state, catalogId));

    const isStoppedFromBidding =
        !accountInGoodStanding ||
        (!approved && !limited) ||
        suspended ||
        declined ||
        blockedByAuctioneer ||
        overBidLimit ||
        !checkReadyToBid({
            catalogId,
            closed,
            currentAskPrice,
            currentItem,
            itemId,
            liveUpdateTimestamp,
            networkStatus,
            status,
            won,
        });

    const onCardClick = useCallback(() => {
        if (isSavedItemsPage(pathname)) {
            trackItemCardClicked({
                catalogId,
                itemId,
                promoted,
                source: ItemCardSource.MyItemsPageFavorites,
            });
        } else if (isMyBidsPage(pathname)) {
            trackItemCardClicked({
                catalogId,
                itemId,
                promoted,
                source: ItemCardSource.MyItemsPageBids,
            });
        }
    }, [catalogId, itemId, pathname, promoted, trackItemCardClicked]);

    const enterHoverState = useCallback(() => {
        setHover(true);
    }, []);

    const leaveHoverState = useCallback(() => {
        setHover(false);
    }, []);

    const enterLinkHoverState = useCallback(() => {
        setLinkHover(true);
    }, []);

    const leaveLinkHoverState = useCallback(() => {
        setLinkHover(false);
    }, []);

    return (
        <article
            className="relative"
            data-testid={`timed-plus-item-card-${itemId}`}
            onClick={onCardClick}
            onMouseEnter={enterHoverState}
            onMouseLeave={leaveHoverState}
        >
            <section
                className={cn(
                    "grid [grid-template-columns:1fr] [grid-template-rows:min-content_min-content_min-content] justify-center [grid-template-areas:'itemimage'_'.'_'alldetailsformobilelayout'] xsMax:[grid-template-columns:1fr_4fr] xsMax:gap-x-sm xsMax:[grid-template-areas:'itemimage_alldetailsformobilelayout']",
                    isDone
                        ? 'xsMax:[grid-template-rows:min-content_11px_min-content_4px_min-content_2px_min-content_0]'
                        : 'xsMax:[grid-template-rows:min-content_min-content]',
                    className
                )}
                data-testid="saved-items-card-grid"
            >
                <CardImage
                    enterLinkHoverState={enterLinkHoverState}
                    isCatalogDone={isDone}
                    isMobile={isMobile}
                    itemId={itemId}
                    leaveLinkHoverState={leaveLinkHoverState}
                />
                <CardGridBody
                    cardHovered={hover}
                    enterHoverState={enterHoverState}
                    enterLinkHoverState={enterLinkHoverState}
                    isCurrentLiveitem={currentItemLive}
                    isMobile={isMobile}
                    isStoppedFromBidding={isStoppedFromBidding}
                    itemId={itemId}
                    leaveHoverState={leaveHoverState}
                    leaveLinkHoverState={leaveLinkHoverState}
                    linkHovered={linkHover}
                />
            </section>
        </article>
    );
};

const info: ItemCardInfo = {
    ItemCardComponent: TimedPlusMyItemsCard,
};

export default info;
